import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import PostCard from "../components/PostCard";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CalendarBlank, MapPin, ArrowUp, ChatCircle } from "@phosphor-icons/react";

export default function ProfilePage() {
    const { id } = useParams();
    const { user: me } = useAuth();
    const [user, setUser] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        (async () => {
            setLoading(true);
            try {
                const [u, p] = await Promise.all([
                    api.get(`/users/${id}`),
                    api.get(`/users/${id}/posts`),
                ]);
                if (!cancel) {
                    setUser(u.data);
                    setPosts(p.data);
                }
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [id]);

    const onUpdate = (updated) => {
        setPosts((cur) => cur.map((p) => (p.id === updated.id ? updated : p)));
    };

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-[#FAFAFA]">
                <Navbar />
                <div className="max-w-5xl mx-auto px-4 py-12 text-center text-stone-500 text-sm">Loading…</div>
            </div>
        );
    }

    const initials = user.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
    const totalUpvotes = posts.reduce((sum, p) => sum + (p.upvotes || 0), 0);
    const totalComments = posts.reduce((sum, p) => sum + (p.comments_count || 0), 0);
    const isMe = me && me.id === user.id;
    const created = new Date(user.created_at);

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="profile-page">
            <Navbar />

            {/* Header */}
            <section className="bg-white border-b border-stone-200">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <div className="flex flex-col sm:flex-row gap-6 items-start">
                        <div className="w-24 h-24 rounded-2xl bg-[#990000] flex items-center justify-center relative">
                            <span className="text-[#FFCC00] font-heading font-extrabold text-3xl">{initials}</span>
                            {user.role === "admin" && (
                                <span className="absolute -top-2 -right-2 bg-[#FFCC00] text-[#5C0000] text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border border-white">
                                    Admin
                                </span>
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#990000] mb-1">USC Trojan</div>
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A] tracking-tight">
                                {user.name} {isMe && <span className="text-stone-400 text-base font-normal">(you)</span>}
                            </h1>
                            {user.bio && <p className="text-stone-600 mt-2 max-w-xl">{user.bio}</p>}
                            <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
                                <span className="inline-flex items-center gap-1">
                                    <CalendarBlank size={14} /> Joined {created.toLocaleString("en-US", { month: "long", year: "numeric" })}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mt-8 max-w-md">
                        <Stat label="Posts" value={posts.length} icon={MapPin} />
                        <Stat label="Upvotes" value={totalUpvotes} icon={ArrowUp} />
                        <Stat label="Comments" value={totalComments} icon={ChatCircle} />
                    </div>
                </div>
            </section>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-600 mb-4">
                    {isMe ? "Your posts" : `${user.name.split(" ")[0]}'s posts`}
                </div>
                {posts.length === 0 ? (
                    <div className="bg-white border border-stone-200 rounded-xl p-12 text-center text-stone-500 text-sm">
                        {isMe ? "You haven't shared any tips yet." : "No posts yet."}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {posts.map((p, i) => (
                            <PostCard key={p.id} post={p} onUpdate={onUpdate} index={i} />
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

function Stat({ label, value, icon: Icon }) {
    return (
        <div className="bg-stone-50 border border-stone-100 rounded-lg p-4">
            <div className="flex items-center gap-2 text-stone-500 mb-1">
                <Icon size={14} />
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold">{label}</div>
            </div>
            <div className="font-heading text-2xl font-bold text-[#0A0A0A]">{value}</div>
        </div>
    );
}
